import { CaptchaType, RecaptchaEnterpriseMode, Theme } from '@logto/schemas';
import { useMemo, useContext, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import useToast from '@/hooks/use-toast';
import { waitForCaptchaResult } from '@/utils/captcha-result';

import PageContext from '../PageContextProvider/PageContext';

import CaptchaContext, {
  captchaCancelled,
  type CaptchaContextType,
  type CaptchaToken,
} from './CaptchaContext';
import { scriptId } from './constant';
import { getScript } from './utils';

type Props = {
  readonly children: React.ReactNode;
};

const CaptchaContextProvider = ({ children }: Props) => {
  const { experienceSettings, theme } = useContext(PageContext);
  const widgetRef = useRef<HTMLDivElement>(null);
  // 阿里云验证码实例，用于程序化弹出和销毁。
  const aliyunInstanceRef = useRef<{ show?: () => void; destroyCaptcha?: () => void } | undefined>(
    undefined
  );
  const { setToast } = useToast();
  const { t } = useTranslation();
  const cancelVerificationRef = useRef<() => void>();

  const captchaPolicy = experienceSettings?.captchaPolicy;
  const captchaConfig = experienceSettings?.captchaConfig;

  const isCaptchaRequired = Boolean(captchaPolicy?.enabled);

  const initCaptcha = useCallback(() => {
    if (!isCaptchaRequired || !captchaConfig) {
      return;
    }

    if (document.querySelector(`#${scriptId}`)) {
      return;
    }

    // 阿里云验证码要求在加载 AliyunCaptcha.js 之前设好全局 AliyunCaptchaConfig（region/prefix）
    if (captchaConfig.type === CaptchaType.AliyunCaptcha) {
      // eslint-disable-next-line @silverhand/fp/no-mutation
      window.AliyunCaptchaConfig = {
        region: captchaConfig.region ?? 'cn',
        prefix: captchaConfig.prefix ?? '',
      };
    }

    const script = document.createElement('script');
    /* eslint-disable @silverhand/fp/no-mutation */
    script.src = getScript(captchaConfig);
    script.id = scriptId;
    script.async = true;
    /* eslint-enable @silverhand/fp/no-mutation */

    document.body.append(script);
  }, [isCaptchaRequired, captchaConfig]);

  const executeCaptcha = useCallback(async () => {
    if (!isCaptchaRequired || !captchaConfig) {
      return;
    }

    if (captchaConfig.type === CaptchaType.Turnstile) {
      return new Promise<string | undefined>((resolve, reject) => {
        if (!window.turnstile || !widgetRef.current) {
          resolve(undefined);
          return;
        }

        // Clear the dom element first
        // eslint-disable-next-line @silverhand/fp/no-mutation
        widgetRef.current.innerHTML = '';

        window.turnstile.render(widgetRef.current, {
          sitekey: captchaConfig.siteKey ?? '',
          theme: theme === Theme.Light ? 'light' : 'dark',
          callback: (token: string) => {
            resolve(token);
          },
          'error-callback': (errorCode) => {
            setToast(t('error.captcha_verification_failed'));
            reject(new Error(`Turnstile error: ${errorCode}`));
          },
          size: 'flexible',
        });
      });
    }

    // 阿里云验证码 2.0：桥接进 Logto 命令式 executeCaptcha。
    // - 每次调用「重新初始化」（上一验证生命周期结束后需重新 init，见阿里云 FAQ Q16），拿到实例后 show() 弹出。
    // - captchaVerifyCallback 拿到 captchaVerifyParam → 作为 Logto token resolve（判定交服务端 VerifyIntelligentCaptcha）。
    // ⚠️ 场景需用「交互式形态（滑块/一点即过）」：无痕形态首次验证不支持程序化 show()（阿里云 FAQ Q13/Q14）。
    if (captchaConfig.type === CaptchaType.AliyunCaptcha) {
      return new Promise<CaptchaToken>((resolve) => {
        const attempt: {
          cancel?: () => void;
          next?: (token?: string) => void;
          verifying?: boolean;
          closed?: boolean;
        } = {};
        const container = widgetRef.current;
        if (!window.initAliyunCaptcha || !container) {
          resolve(undefined);
          return;
        }

        // 销毁上一实例并重建 element + 隐藏 button（button 必传，可为隐藏元素）
        cancelVerificationRef.current?.();
        aliyunInstanceRef.current?.destroyCaptcha?.();
        // 新实例异步返回前，不能让轮询调用已销毁实例的 show()。
        // eslint-disable-next-line @silverhand/fp/no-mutation
        aliyunInstanceRef.current = undefined;
        // eslint-disable-next-line @silverhand/fp/no-mutation
        container.innerHTML =
          '<div id="aliyun-captcha-element"></div>' +
          '<button id="aliyun-captcha-button" type="button" style="display:none">captcha</button>';

        window.initAliyunCaptcha({
          SceneId: captchaConfig.sceneId ?? '',
          prefix: captchaConfig.prefix ?? '',
          mode: 'popup',
          element: '#aliyun-captcha-element',
          button: '#aliyun-captcha-button',
          language: 'cn',
          region: captchaConfig.region ?? 'cn',
          immediate: true, // 提交验证参数后仍须等待业务服务端返回校验结果
          disableMaskClick: true,
          onClose() {
            // eslint-disable-next-line @silverhand/fp/no-mutation
            attempt.closed = true;
            attempt.cancel?.();
            attempt.next?.();
            resolve(captchaCancelled);
          },
          async captchaVerifyCallback(captchaVerifyParam: string) {
            if (attempt.verifying === true || attempt.closed === true) {
              return { captchaResult: false };
            }
            const previousNext = attempt.next;
            const nextToken = new Promise<string | undefined>((resolve) => {
              // eslint-disable-next-line @silverhand/fp/no-mutation
              attempt.next = resolve;
            });
            const verification = waitForCaptchaResult(captchaVerifyParam, nextToken);
            /* eslint-disable @silverhand/fp/no-mutation */
            attempt.verifying = true;
            attempt.cancel = verification.cancel;
            cancelVerificationRef.current = () => {
              attempt.closed = true;
              verification.cancel();
              attempt.next?.();
              resolve(captchaCancelled);
            };
            /* eslint-enable @silverhand/fp/no-mutation */
            if (previousNext) {
              previousNext(captchaVerifyParam);
            } else {
              resolve(captchaVerifyParam);
            }
            const captchaResult = await verification.result;
            // Keep the widget open: the SDK displays failure and lets the user try another puzzle.
            // eslint-disable-next-line @silverhand/fp/no-mutation
            attempt.verifying = false;
            return { captchaResult };
          },
          onBizResultCallback() {
            // 业务结果由 Logto 后续流程处理，无需在此干预
          },
          getInstance(instance) {
            // eslint-disable-next-line @silverhand/fp/no-mutation
            aliyunInstanceRef.current = instance;
          },
          slideStyle: { width: 360, height: 40 },
        });

        // Init 是异步的：轮询等实例就绪后 show() 弹出（滑块等交互式形态支持）
        // eslint-disable-next-line @silverhand/fp/no-let
        let tries = 0;
        const timer = window.setInterval(() => {
          // eslint-disable-next-line @silverhand/fp/no-mutation
          tries += 1;
          if (aliyunInstanceRef.current?.show) {
            window.clearInterval(timer);
            aliyunInstanceRef.current.show();
          } else if (tries > 50) {
            // ~5s 仍未就绪：放弃（脚本没加载/被拦截），resolve undefined 让上层按缺 token 处理
            window.clearInterval(timer);
            resolve(undefined);
          }
        }, 100);
      });
    }

    if (!window.grecaptcha?.enterprise) {
      return;
    }

    // Handle checkbox mode for reCAPTCHA Enterprise
    if (captchaConfig.mode === RecaptchaEnterpriseMode.Checkbox) {
      return new Promise<string | undefined>((resolve, reject) => {
        if (!window.grecaptcha || !widgetRef.current) {
          resolve(undefined);
          return;
        }

        // Clear the dom element first
        // eslint-disable-next-line @silverhand/fp/no-mutation
        widgetRef.current.innerHTML = '';

        window.grecaptcha.enterprise.render(widgetRef.current, {
          sitekey: captchaConfig.siteKey ?? '',
          theme: theme === Theme.Light ? 'light' : 'dark',
          callback: (token: string) => {
            resolve(token);
          },
          'error-callback': (errorCode) => {
            setToast(t('error.captcha_verification_failed'));
            reject(new Error(`reCAPTCHA error: ${errorCode}`));
          },
        });
      });
    }

    // Default invisible mode
    return window.grecaptcha.enterprise.execute(captchaConfig.siteKey ?? '', {
      action: 'interaction',
    });
  }, [isCaptchaRequired, captchaConfig, theme, setToast, t]);

  useEffect(() => {
    initCaptcha();
  }, [initCaptcha]);

  useEffect(() => () => cancelVerificationRef.current?.(), []);

  const captchaContext = useMemo<CaptchaContextType>(
    () => ({
      isCaptchaRequired,
      executeCaptcha,
      captchaConfig,
      widgetRef,
    }),
    [isCaptchaRequired, executeCaptcha, captchaConfig, widgetRef]
  );

  return <CaptchaContext.Provider value={captchaContext}>{children}</CaptchaContext.Provider>;
};

export default CaptchaContextProvider;
